// @flow

import React from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import actionCreators from "@modules/shared/actionCreator";
import {
  Button,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
  LinearWizard,
  Step,
} from "@components";
import type { State } from "@store/reducers";
import type {
  AddNotificationModal,
  EmployeeRefType,
} from "@modules/shared/reducer/employee-notifications/types";
import type { EmployeeType } from "@modules/employee/types";
import type { PropertiesSchema } from "@types";
import { EMPLOYEE_REF_TYPES } from "@modules/shared/constants/notifications";
import {
  getAddNotificationModal,
  getNotificationSchema,
  getNotificationDateFields,
} from "@modules/shared/selectors/employee-notifications";
import { formatDateToInputFormat } from "@transformers/date";
import SelectEmployee from "./select-employee";
import Step2ConfigureNotification from "./step2-configure-notification";
import Step2ConfigureDates from "./step2-configure-dates";

type OwnProps = $Exact<{
  clientId: number,
  employeeId: number,
  employeeRefType: EmployeeRefType,
  employee?: EmployeeType,
  isNotificationContact?: boolean,
  isLoading?: boolean,
  refreshAction?: () => void,
}>;

type PropsFromState = $Exact<{
  modal: AddNotificationModal,
  notificationSchema: ?PropertiesSchema,
  notificationDateFields: Object,
}>;

type PropsFromDispatch = $Exact<{
  toggleAddNotificationModal: typeof actionCreators.ToggleAddNotificationModal,
  toggleEditNotificationModal: typeof actionCreators.ToggleEditNotificationModal,
  createNotification: typeof actionCreators.CreateNotification,
  createNotificationTarget: typeof actionCreators.CreateNotificationTarget,
  updateNotification: typeof actionCreators.UpdateNotification,
  updateNotificationTarget: typeof actionCreators.UpdateNotificationTarget,
  getNotificationSchema: typeof actionCreators.GetNotificationSchema,
  updateManagerNotification: typeof actionCreators.UpdateManagerNotification,
  createManagerNotification: typeof actionCreators.CreateManagerNotification,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

type ComponentState = $Exact<{
  formData: Array<Object>,
  canSubmit: boolean,
}>;

export class NotificationsAddModal extends React.Component<Props, ComponentState> {
  state = {
    formData: [],
    canSubmit: false,
  };

  componentDidMount() {
    if (!this.props.notificationSchema) {
      this.props.getNotificationSchema();
    }
  }

  handleCancel = () => {
    this.setState({
      formData: [],
      canSubmit: false,
    });
    this.props.modal.editing
      ? this.props.toggleEditNotificationModal({ visible: false })
      : this.props.toggleAddNotificationModal(false);
  };

  handleSubmit = () => {
    if (this.props.modal.editing) {
      this.handleUpdateNotification(this.state.formData[0], this.state.formData[1]);
      return;
    }

    this.handleCreateNotification(this.state.formData[0], this.state.formData[1]);
  };

  handleWizardChange = (data?: Array<Object>) => {
    this.setState({
      formData: data,
    });
  };

  handleCreateNotification = (employee: EmployeeType, notificationData: Object) => {
    const notification = {
      ...notificationData,
      recipient: {
        id: employee.id,
      },
    };

    if (this.props.isNotificationContact) {
      notification.activeFrom = formatDateToInputFormat(
        this.props.notificationDateFields.activeFrom,
      );
      notification.activeTo = formatDateToInputFormat(this.props.notificationDateFields.activeTo);
    }

    const data = {
      clientId: this.props.clientId,
      employeeId: this.props.employeeId,
      notification,
      refreshAction: this.props.refreshAction,
    };
    if (this.props.employeeRefType === EMPLOYEE_REF_TYPES.RECIPIENT) {
      this.props.createNotification(data);
    } else if (this.props.employeeRefType === EMPLOYEE_REF_TYPES.TARGET) {
      this.props.createNotificationTarget(data);
    } else if (this.props.employeeRefType === EMPLOYEE_REF_TYPES.MANAGER) {
      this.props.createManagerNotification(data);
    }
  };

  handleUpdateNotification = (employee: EmployeeType, notificationData: Object) => {
    const notification = {
      id: this.props.modal.data.notificationId,
      ...notificationData,
      recipient: {
        id: employee.id,
      },
    };

    if (this.props.isNotificationContact) {
      notification.activeFrom = formatDateToInputFormat(
        this.props.notificationDateFields.activeFrom,
      );
      notification.activeTo = formatDateToInputFormat(this.props.notificationDateFields.activeTo);
    }

    const data = {
      clientId: this.props.clientId,
      employeeId: this.props.employeeId,
      notification,
    };

    if (this.props.modal.data.employeeRefType === EMPLOYEE_REF_TYPES.RECIPIENT) {
      if (this.props.employeeRefType === EMPLOYEE_REF_TYPES.MANAGER) {
        this.props.updateManagerNotification(data);
      } else {
        this.props.updateNotification(data);
      }
    } else if (this.props.modal.data.employeeRefType === EMPLOYEE_REF_TYPES.TARGET) {
      this.props.updateNotificationTarget(data);
    }
  };

  get notificationTypes() {
    const notificationTypes =
      (((this.props.notificationSchema || {}).properties || {}).notificationType || {}).oneOf || [];
    const sortedNotifications = [];
    if (notificationTypes) {
      notificationTypes.forEach(item => {
        switch (item.const) {
          case "BASIC_NOTIFICATION":
            sortedNotifications[0] = item;
            break;
          case "STANDARD_NOTIFICATION":
            sortedNotifications[1] = item;
            break;
          case "FULL_NOTIFICATION":
            sortedNotifications[2] = item;
            break;
          default:
            sortedNotifications.push(item);
            break;
        }
      });
    }

    return sortedNotifications;
  }

  get wizardData() {
    if (!this.props.modal.editing) {
      return [{ data: {}, valid: false }];
    }

    return [
      {
        data: { ...this.props.modal.data.employeeDetails },
        valid: true,
      },
      {
        data: { notificationType: this.props.modal.data.notificationType },
        valid: true,
      },
    ];
  }

  get submitButtonTitle() {
    return this.props.modal.editing ? this.props.t("EDIT") : this.props.t("ADD");
  }

  handleActionConfirm = (value: string) => {
    this.setState({
      canSubmit: value === "true" ? true : false,
    });
  };

  get titles() {
    return {
      recipient: {
        modalTitle: this.props.modal.editing
          ? this.props.t("NOTIFICATIONS_EDIT_RECIPIENT")
          : this.props.t("NOTIFICATIONS_ADD_RECIPIENT"),
        stepTitle: this.props.t("SELECT_CONTACT"),
        confirmationText: "NOTIFICATION_CONTACT_UPDATE_CONFIRMATION_WARNING_RECIPIENT",
      },
      manager: {
        modalTitle: this.props.modal.editing
          ? this.props.t("NOTIFICATIONS_EDIT_RECIPIENT")
          : this.props.t("NOTIFICATIONS_ADD_RECIPIENT"),
        stepTitle: this.props.t("SELECT_CONTACT"),
        confirmationText: "NOTIFICATION_CONTACT_UPDATE_CONFIRMATION_WARNING_RECIPIENT",
      },
      target: {
        modalTitle: this.props.modal.editing
          ? this.props.t("EDIT_EMPLOYEE")
          : this.props.t("ADD_EMPLOYEE"),
        stepTitle: this.props.t("SELECT_EMPLOYEE"),
        confirmationText: "NOTIFICATION_CONTACT_UPDATE_CONFIRMATION_WARNING_TARGET",
      },
    };
  }

  render() {
    if (!this.props.modal.visible) {
      return null;
    }

    return (
      <Modal className="modal-large">
        <ModalHeader className="d-flex justify-content-between" toggle={this.handleCancel}>
          {this.titles[this.props.employeeRefType].modalTitle}
        </ModalHeader>
        <ModalBody className="d-flex flex-column justify-content-start align-content-center p-0">
          <LinearWizard
            onChange={this.handleWizardChange}
            defaultStep={this.props.modal.editing ? 2 : 1}
            data={this.wizardData}
          >
            <Step
              component={SelectEmployee}
              title={this.titles[this.props.employeeRefType].stepTitle}
              clientId={this.props.clientId}
              isReadonly={this.props.modal.editing}
              employeeRefType={this.props.employeeRefType}
              isLoading={this.props.isLoading}
            />
            {this.props.isNotificationContact ? (
              <Step
                component={Step2ConfigureDates}
                title={this.props.t("CONFIGURE_CONTACT")}
                notificationTypes={this.notificationTypes}
                employeeToAdd={this.state.formData[0]}
                notificationData={this.props.modal.editing ? this.props.modal.data : null}
                isOpen={this.props.modal.visible}
                recipient={this.props.employee}
              />
            ) : (
              <Step
                component={Step2ConfigureNotification}
                title={this.props.t("CONFIGURE_NOTIFICATION")}
                notificationTypes={this.notificationTypes}
                employeeToAdd={this.state.formData[0]}
                notificationData={this.props.modal.editing ? this.props.modal.data : null}
                isOpen={this.props.modal.visible}
                isCheckConfirmed={this.state.canSubmit}
                handleCheckConfirm={this.handleActionConfirm}
                recipient={this.props.employee}
                employeeRefType={this.props.employeeRefType}
                confirmationText={this.titles[this.props.employeeRefType].confirmationText}
              />
            )}
          </LinearWizard>
        </ModalBody>
        <ModalFooter className="d-flex justify-content-end">
          <Button color="secondary" onClick={this.handleCancel}>
            {this.props.t("CANCEL")}
          </Button>
          <Button
            materialIcon={this.props.modal.editing ? "edit" : "add_box"}
            onClick={this.handleSubmit}
            disabled={!this.state.canSubmit}
          >
            {this.submitButtonTitle}
          </Button>
        </ModalFooter>
      </Modal>
    );
  }
}

const mapStateToProps = (state: State): PropsFromState => ({
  modal: getAddNotificationModal(state), // TODO: should me moved one level up
  notificationSchema: getNotificationSchema(state),
  notificationDateFields: getNotificationDateFields(state),
});

const mapDispatchToProps: PropsFromDispatch = {
  toggleAddNotificationModal: actionCreators.ToggleAddNotificationModal,
  toggleEditNotificationModal: actionCreators.ToggleEditNotificationModal,
  createNotification: actionCreators.CreateNotification,
  createNotificationTarget: actionCreators.CreateNotificationTarget,
  updateNotification: actionCreators.UpdateNotification,
  updateNotificationTarget: actionCreators.UpdateNotificationTarget,
  getNotificationSchema: actionCreators.GetNotificationSchema,
  updateManagerNotification: actionCreators.UpdateManagerNotification,
  createManagerNotification: actionCreators.CreateManagerNotification,
};

export default compose(
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
)(NotificationsAddModal);
